import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChatStore } from '../store/useChatStore';
import SetTokenButton from './SetTokenButton';

const STARTERS = [
  { label: 'IP sequence search', prompt: 'Run an IP sequence search for this DNA: ATCGGCTAGCTAGCTATCGATCG' },
  { label: 'Antibody search', prompt: 'Search for antibody patents matching VH sequence: EVQLVESGGGLVQPGG' },
  { label: 'Text search', prompt: 'Find CRISPR patents assigned to Broad Institute in US and EP' },
  { label: 'List my workflows', prompt: 'List my recent GQ workflows' },
];

export default function ChatPane() {
  const { messages, streaming, streamingContent, sendMessage } = useChatStore();
  const [input, setInput] = useState('');
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  const submit = () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    sendMessage(text);
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    // Enter sends; Shift+Enter inserts a newline. Handle both explicitly (and stop
    // the event from bubbling) so Shift+Enter works even if an app-level key handler
    // or the environment interferes with the textarea's native newline.
    e.stopPropagation();
    e.preventDefault();
    if (!e.shiftKey) {
      submit();
      return;
    }
    const el = e.currentTarget;
    el.setRangeText('\n', el.selectionStart, el.selectionEnd, 'end');
    setInput(el.value);
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  };

  const isEmpty = messages.length === 0;

  return (
    <main style={{
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: 'var(--gqs-bg)',
    }}>
      {/* Pane header — title + Set GQ Token control (replaces the app's old top NavBar) */}
      <div style={{
        height: 40, flexShrink: 0,
        borderBottom: '1px solid var(--gqs-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 14px',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>GQ Search Assistant</span>
        <SetTokenButton />
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {isEmpty && !streaming ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 40, gap: 24 }}>
            <div style={{ textAlign: 'center' }}>
              <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>GQ Search Assistant</h1>
              <p style={{ color: 'var(--gqs-text-muted)', fontSize: 14 }}>
                Search GenomeQuest patent sequence databases through natural conversation.
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 540 }}>
              {STARTERS.map((s) => (
                <button
                  key={s.label}
                  onClick={() => { setInput(s.prompt); textareaRef.current?.focus(); }}
                  style={{
                    background: 'var(--gqs-surface)',
                    border: '1px solid var(--gqs-border)',
                    borderRadius: 8,
                    padding: '8px 14px',
                    color: 'var(--gqs-text)',
                    cursor: 'pointer',
                    fontSize: 13,
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--gqs-accent)')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--gqs-border)')}
                >{s.label}</button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <MessageBubble key={msg.id} role={msg.role} content={msg.content} workflowId={msg.workflowId} />
            ))}
            {streaming && streamingContent && (
              <MessageBubble role="assistant" content={streamingContent} streaming />
            )}
            {streaming && !streamingContent && (
              <div style={{ display: 'flex', gap: 5, padding: '8px 0', paddingLeft: 4 }} aria-label="Agent is typing">
                {[0, 1, 2].map((i) => (
                  <span key={i} style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: 'var(--gqs-text-muted)',
                    animation: `gqsPulse 1.4s ${i * 0.2}s infinite ease-in-out`,
                    display: 'inline-block',
                  }} />
                ))}
              </div>
            )}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div style={{
        borderTop: '1px solid var(--gqs-border)',
        padding: '12px 16px',
        background: 'var(--gqs-surface)',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-end',
      }}>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask GQ Search Assistant… (Shift+Enter for new line)"
          rows={1}
          disabled={streaming}
          style={{
            flex: 1,
            background: 'var(--gqs-bg)',
            border: '1px solid var(--gqs-border)',
            borderRadius: 8,
            padding: '9px 12px',
            color: 'var(--gqs-text)',
            fontSize: 14,
            resize: 'none',
            outline: 'none',
            minHeight: 38,
            maxHeight: 140,
            overflowY: 'auto',
            lineHeight: 1.5,
            fontFamily: 'inherit',
          }}
          onInput={(e) => {
            const el = e.currentTarget;
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 140) + 'px';
          }}
        />
        <button
          onClick={submit}
          disabled={!input.trim() || streaming}
          aria-label="Send message"
          style={{
            background: !input.trim() || streaming ? 'var(--gqs-border)' : 'var(--gqs-accent-solid)',
            border: 'none',
            borderRadius: 8,
            width: 38,
            height: 38,
            cursor: !input.trim() || streaming ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 0.15s',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M22 2L11 13M22 2L15 22 11 13 2 9l20-7z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <style>{`
        @keyframes gqsPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.85); }
          40% { opacity: 1; transform: scale(1); }
        }
        @keyframes gqsBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
      `}</style>
    </main>
  );
}

function MessageBubble({ role, content, workflowId, streaming }) {
  const isUser = role === 'user';

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 14,
    }}>
      {!isUser && (
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'var(--gqs-accent-soft-strong)',
          border: '1px solid var(--gqs-accent-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, marginRight: 10, marginTop: 2,
          fontSize: 12, color: 'var(--gqs-accent)',
        }}>G</div>
      )}

      <div style={{
        maxWidth: '78%',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        alignItems: isUser ? 'flex-end' : 'flex-start',
      }}>
        <div style={{
          background: isUser ? 'var(--gqs-accent-solid)' : 'var(--gqs-surface-raised)',
          border: isUser ? 'none' : '1px solid var(--gqs-border)',
          borderRadius: isUser ? '14px 14px 4px 14px' : '4px 14px 14px 14px',
          padding: '9px 13px',
          color: isUser ? 'var(--gqs-on-accent)' : 'var(--gqs-text)',
          fontSize: 14,
          lineHeight: 1.55,
          position: 'relative',
        }}>
          {isUser ? (
            <span style={{ whiteSpace: 'pre-wrap' }}>{content}</span>
          ) : (
            <div className="gqs-prose">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          )}
          {streaming && (
            <span style={{
              display: 'inline-block', width: 2, height: 14,
              background: 'var(--gqs-text-muted)',
              marginLeft: 2, verticalAlign: 'text-bottom',
              animation: 'gqsBlink 1s step-end infinite',
            }} />
          )}
        </div>

        {workflowId && (
          <span style={{
            fontSize: 10, color: 'var(--gqs-accent)',
            background: 'var(--gqs-accent-soft)',
            border: '1px solid var(--gqs-accent-border)',
            borderRadius: 4,
            padding: '1px 6px',
          }}>WF {workflowId}</span>
        )}
      </div>
    </div>
  );
}
